import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { app_paths } from './variables';
import { FaUserDoctor } from "react-icons/fa6";
import ListAltIcon from '@mui/icons-material/ListAlt';
import { FaRegCalendarAlt } from "react-icons/fa";
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

/**
 * Navigation configuration for each user role
 *
 * Structure:
 * - id: unique identifier
 * - label: display text
 * - icon: MUI icon component
 * - path: route path (from app_paths)
 * - children: nested menu items (optional)
 * - showPatientList: special flag to render patient list component (optional)
 */

export const getNavigationConfig = (userRole, userName) => {
  const baseConfig = {
    doctor: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: DashboardIcon,
        path: '/',
      },
      {
        id: 'patients',
        label: 'Patients',
        icon: PeopleIcon,
        path: app_paths.PATIENTS, // Parent is now clickable
        children: [
          {
            id: 'patient-list',
            label: 'Patient List',
            showPatientList: true, // Special flag to render the patient list component
          },
          // {
          //   id: 'new-patient',
          //   label: 'New Patient',
          //   icon: PersonAddIcon,
          //   path: app_paths.NEW_PATIENT,
          // },
        ],
      },
      {
        id: 'ai-assistant',
        label: 'NLP',
        icon: PsychologyIcon,
        path: app_paths.NLP,
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: PersonIcon,
        path: `/doctor/${userName}`,
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        path: app_paths.SETTINGS,
      },
      {
        id: 'doctor-appointment',
        label: 'Appointment',
        icon: FaRegCalendarAlt,
        path: app_paths.DOCTOR_APPOINTMENT,
      },
      {
        id: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        isLogout: true,
      },
    ],

    insurance_admin: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: DashboardIcon,
        path: '/',
      },
      {
        id: 'patients',
        label: 'Patients',
        icon: PeopleIcon,
        path: app_paths.PATIENTS, // Parent is now clickable
        children: [
          {
            id: 'patient-list',
            label: 'Patient List',
            showPatientList: true,
          },
        ],
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChartIcon,
        path: app_paths.INSURANCE_ANALYSIS,
      },
      {
        id: 'prices',
        label: 'Prices',
        icon: ListAltIcon ,
        path: app_paths.PRICES_PAGE,
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        path: app_paths.SETTINGS,
      },
      {
        id: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        isLogout: true,
      },
    ],

    facility_admin: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: DashboardIcon,
        path: '/',
      },
      {
        id: 'patients',
        label: 'Patients',
        icon: PeopleIcon,
        path: app_paths.PATIENTS, // Parent is now clickable
        children: [
          {
            id: 'patient-list',
            label: 'Patient List',
            showPatientList: true,
          },
        ],
      },
      {
        id: 'doctors',
        label: 'Doctors',
        icon: FaUserDoctor,
        path: app_paths.DOCTOR_LIST,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChartIcon,
        path: app_paths.INSURANCE_ANALYSIS,
      },
      {
        id: 'prices',
        label: 'Prices',
        icon: ListAltIcon ,
        path: app_paths.PRICES_PAGE,
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        path: app_paths.SETTINGS,
      },
      {
        id: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        isLogout: true,
      },
    ],

    pharmacy_admin: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: DashboardIcon,
        path: '/',
      },
      {
        id: 'inventory',
        label: 'Inventory',
        icon: ListAltIcon ,
        path: app_paths.INVENTORY_PAGE,
      },
      {
        id: 'encounter-history',
        label: 'Encounter History',
        icon: MedicalServicesIcon,
        path: app_paths.PHARMACY_ENCOUNTER_HISTORY,
      },
      {
        id: 'patients',
        label: 'Patients',
        icon: PeopleIcon,
        path: app_paths.PATIENTS, // Parent is now clickable
        children: [
          {
            id: 'patient-list',
            label: 'Patient List',
            showPatientList: true,
          },
        ],
      },
      {
        id: 'ai-assistant',
        label: 'AI Assistant',
        icon: PsychologyIcon,
        path: app_paths.NLP,
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        path: app_paths.SETTINGS,
      },
      {
        id: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        isLogout: true,
      },
    ],

    lab_admin: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: DashboardIcon,
        path: '/',
      },
      {
        id: 'Lab-Test-Results',
        label: 'Lab Test Results',
        icon: ListAltIcon,
        path: app_paths.LAB_TESTS_RESULTS,
      },
      {
        id: 'encounter-history',
        label: 'Encounter History',
        icon: MedicalServicesIcon,
        path: app_paths.LAB_ENCOUNTER_HISTORY,
      },
      {
        id: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        isLogout: true,
      },
    ],

    patient: [
      {
        id: 'my-portal',
        label: 'My Portal',
        icon: DashboardIcon,
        path: '/',
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        path: app_paths.SETTINGS,
      },
      {
        id: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        isLogout: true,
      },
    ],
  };

  return baseConfig[userRole] || baseConfig.patient;
};

export default getNavigationConfig;
